import React, { ReactNode } from 'react';
import { useNode } from '@craftjs/core';

type Align = 'stretch' | 'start' | 'center' | 'end';

interface GridProps {
  children?: ReactNode;
  columns?: number;
  gap?: number;
  rowMinHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  background?: string;
  padding?: number;
  margin?: number;
  borderRadius?: number;
  justifyItems?: Align;
  alignItems?: Align;
  /**
   * Cuántas columnas abajo de 768px. Ausente = las mismas que `columns`, que es
   * lo que hacían TODAS las grillas hasta ahora: `gridTemplateColumns` es un
   * estilo inline y no tiene forma de cambiar por ancho de pantalla.
   *
   * Sólo cuando alguien la define —y es distinta de `columns`— se emite la
   * media query. Sin la prop no se agrega ni una clase ni una etiqueta, así que
   * el HTML de lo ya publicado es el mismo carácter por carácter.
   */
  columnsMobile?: number;
}

// Render del sitio público para la grilla auto-flujo (espejo de goautos-admin).
export const Grid = ({
  children,
  columns = 3,
  gap = 16,
  rowMinHeight = 100,
  maxWidth = 0,
  maxHeight = 0,
  background = 'transparent',
  padding = 16,
  margin = 0,
  borderRadius = 0,
  justifyItems = 'stretch',
  alignItems = 'stretch',
  columnsMobile,
}: GridProps) => {
  const { connectors, selected, id } = useNode((node) => ({
    selected: node.events.selected,
  }));

  const cols = Math.max(1, Math.min(12, Number(columns) || 1));

  // El corte va en 768: es el ancho donde la grilla deja de tener espacio para
  // dos tarjetas legibles. No se usa el `xl` (1280) de las piezas libres, que
  // es otra cosa — ahí lo que cambia son las coordenadas, no las columnas.
  const colsMovil = Number(columnsMobile) > 0
    ? Math.max(1, Math.min(12, Number(columnsMobile)))
    : 0;
  const claseMovil = colsMovil && colsMovil !== cols ? `grilla-movil-${id}` : '';

  // 0 = sin límite. Espejo de goautos-admin.
  const maxW = Number(maxWidth) || 0;
  const maxH = Number(maxHeight) || 0;

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      className={claseMovil || undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridAutoRows: `minmax(${rowMinHeight}px, auto)`,
        gap: `${gap}px`,
        background,
        padding: `${padding}px`,
        margin: maxW > 0 ? `${margin}px auto` : `${margin}px 0`,
        ...(maxW > 0 ? { maxWidth: `${maxW}px`, width: '100%' } : {}),
        ...(maxH > 0 ? { maxHeight: `${maxH}px`, overflowY: 'auto' as const } : {}),
        borderRadius: `${borderRadius}px`,
        justifyItems,
        alignItems,
        minHeight: '80px',
        position: 'relative',
        border: selected ? '2px dashed #666666' : '1px solid transparent',
      }}
    >
      {/*
        `display:none` de fábrica, así que no llega a ser un ítem de la grilla.
        `!important` porque `gridTemplateColumns` va inline y un estilo inline
        le gana a cualquier selector.
      */}
      {claseMovil && (
        <style>{`@media (max-width:767px){.${claseMovil}{grid-template-columns:repeat(${colsMovil},minmax(0,1fr))!important}}`}</style>
      )}
      {children}
    </div>
  );
};

Grid.craft = {
  displayName: 'Grid',
  props: {
    columns: 3,
    gap: 16,
    rowMinHeight: 100,
    maxWidth: 0,
    maxHeight: 0,
    background: 'transparent',
    padding: 16,
    margin: 0,
    borderRadius: 0,
    justifyItems: 'stretch',
    alignItems: 'stretch',
  },
  rules: { canDrop: () => true },
};
